import { useCallback, useEffect, useRef, useState } from "react"
import { useCallSheetData } from "./useCallSheetData"
import { NON_BILLABLE_OPTIONS } from "../constants"
import { useCallFormState } from "./useCallFormState"
import useHospitalData from "hooks/useHospitalData"

export const useCallFormFields = (answeredCall, setServerState) => {
  const [callerAccessCodeId, setCallerAccessCodeId] = useState(null)
  const [meetingUrl, setMeetingUrl] = useState(null)
  const [showCollectLocation, setShowCollectLocation] = useState(false)
  const [showCollectDepartment, setShowCollectDepartment] = useState(false)
  const { callSheetData, getCallSheetData } = useCallSheetData(answeredCall?.interaction?.id)
  // The account the call came in on. The task often doesn't have it, so fall back to the call sheet.
  // Without it, a location saved by another agent looks like "nothing selected".
  const originalPartnerId = answeredCall?.interaction?.originalClientId ?? callSheetData?.originalClientId ?? null
  // Bumped where the sheet is copied into the form, so a reader seeing a new version also sees the
  // sheet's partnerId in the form rather than the previous one.
  const [callSheetVersion, setCallSheetVersion] = useState(0)
  const appliedCallSheetRef = useRef(null)
  const interactionPartnerId = answeredCall?.interaction?.hospitalId ?? callSheetData?.partnerId ?? null
  const { hospital: originalPartner } = useHospitalData(originalPartnerId ?? null)
  const { hospital: interactionPartner } = useHospitalData(
    originalPartnerId ? null : (interactionPartnerId ?? null)
  )

  const { reset, onCallSheetLoad, ...rest } = useCallFormState()

  const { setInteractionId, setInteractionName, setUnitId, setCustomerPhoneNumber, setLanguage } = rest

  const refreshCallSheet = useCallback(() => {
    return getCallSheetData()
  }, [getCallSheetData])

  const lastInteractionIdRef = useRef(null)

  useEffect(() => {
    if (!(answeredCall?.interaction || answeredCall?.language)) {
      return
    }
    const { id, name, unitId, customerPhoneNumber } = answeredCall.interaction
    if (id) {
      if (lastInteractionIdRef.current && lastInteractionIdRef.current !== id) {
        reset()
      }
      lastInteractionIdRef.current = id
      setInteractionId(id)
    }
    setInteractionName(name)
    setUnitId(unitId || "")
    setCustomerPhoneNumber(customerPhoneNumber)
    setLanguage(answeredCall.language?.name)
  }, [
    answeredCall?.interaction,
    answeredCall?.language,
    reset,
    setCustomerPhoneNumber,
    setInteractionId,
    setInteractionName,
    setLanguage,
    setUnitId,
  ])

  useEffect(() => {
    if (callSheetData) {
      const {
        partnerId,
        departmentId,
        providerFirstName,
        providerLastName,
        patientFirstName,
        patientLastName,
        patientMRN,
        patientDOB,
        nonBillableReason,
        phoneNumberAC,
        bchOaklandHospitalConfirmation,
        location,
        memberIdNumber,
        nyuAccessCode,
        sentaraAccessCode,
        visitId,
        callerFirstName,
        callerLastName,
        meetingUrl,
        showCollectLocation: collectLocation,
        showCollectDepartment: collectDepartment,
      } = callSheetData
      setCallerAccessCodeId(callSheetData.accessCodeId)
      setShowCollectLocation(collectLocation ?? false)
      setShowCollectDepartment(collectDepartment ?? false)

      const treeParentId = originalPartnerId ?? partnerId
      const partnerIdForDropdown = partnerId === treeParentId ? "" : (partnerId || "")

      // The department list depends on the location, so a collected department waits for one.
      // An assigned (not collected) department stays, or a save before the location would clear it.
      const isAwaitingLocationSelection = collectLocation && partnerId === treeParentId
      const departmentIdForDropdown =
        isAwaitingLocationSelection && collectDepartment === true ? "" : (departmentId || "")

      onCallSheetLoad({ ...callSheetData, partnerId: partnerIdForDropdown, departmentId: departmentIdForDropdown })
      setMeetingUrl(meetingUrl)
      setServerState({
        partnerId: partnerIdForDropdown,
        nonBillableReason: nonBillableReason === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReason,
        departmentId: departmentIdForDropdown,
        patientFirstName: patientFirstName || "",
        patientLastName: patientLastName || "",
        patientMRN: patientMRN,
        patientDOB: patientDOB || null,
        providerFirstName: providerFirstName || "",
        providerLastName: providerLastName || "",
        phoneNumberAC: phoneNumberAC || "",
        bchOaklandHospitalConfirmation: bchOaklandHospitalConfirmation || false,
        location: location || "",
        memberIdNumber: memberIdNumber || "",
        nyuAccessCode: nyuAccessCode || "",
        sentaraAccessCode: sentaraAccessCode || "",
        visitId: visitId || "",
        callerFirstName: callerFirstName || "",
        callerLastName: callerLastName || "",
      })
      if (appliedCallSheetRef.current !== callSheetData) {
        appliedCallSheetRef.current = callSheetData
        setCallSheetVersion((version) => version + 1)
      }
    }
  }, [callSheetData, onCallSheetLoad, originalPartnerId, setServerState])

  useEffect(() => {
    if (!answeredCall) {
      setCallerAccessCodeId(null)
      setMeetingUrl(null)
      setShowCollectLocation(false)
      setShowCollectDepartment(false)
      reset()
      lastInteractionIdRef.current = null
      return
    }
  }, [answeredCall, reset])

  return {
    ...rest,
    interactionPartnerId,
    originalPartnerId,
    originalPartnerName: originalPartner?.clientName ?? callSheetData?.partnerFriendlyName ?? "",
    accessCodeValidationRequired:
      originalPartner?.accessCodeValidationRequired ?? interactionPartner?.accessCodeValidationRequired,
    refreshCallSheet,
    callSheetVersion,
    callerAccessCodeId,
    showCollectLocation,
    showCollectDepartment,
    setShowCollectDepartment,
    meetingUrl,
  }
}
